package com.anjuros.website.redesign.styles

import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.theme.RdFont
import com.anjuros.website.redesign.theme.RdSize
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

// ---------- Hero: title, search and topic buttons ----------

val FaqHeroStyle = CssStyle.base {
    Modifier.css("padding-block" to "clamp(40px, 6vw, 72px) clamp(28px, 4vw, 44px)")
}

val FaqHeroInnerStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(22.px)
}

val FaqSearchStyle = CssStyle {
    base { Modifier.position(Position.Relative).display(DisplayStyle.Block).css("max-width" to "560px") }
    cssRule(" svg") {
        Modifier.position(Position.Absolute).color(RdColor.Muted)
            .css("left" to "16px", "top" to "50%", "transform" to "translateY(-50%)", "pointer-events" to "none")
    }
    cssRule(" input") {
        Modifier.backgroundColor(RdColor.Paper).color(RdColor.Ink).border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(12.px)
            .css("width" to "100%", "font" to "inherit", "padding" to "15px 16px 15px 48px", "box-shadow" to RdSize.SHADOW)
    }
    cssRule(" input:focus") { Modifier.css("outline" to "2px solid rgb(46,75,110)", "outline-offset" to "0", "border-color" to "transparent") }
}

/** Visually hidden but read by screen readers. */
val FaqVisuallyHiddenStyle = CssStyle.base {
    Modifier.position(Position.Absolute)
        .css("width" to "1px", "height" to "1px", "overflow" to "hidden", "clip" to "rect(0 0 0 0)", "white-space" to "nowrap")
}

val FaqChipsStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).gap(8.px).css("flex-wrap" to "wrap", "list-style" to "none", "margin" to "0", "padding" to "0") }
    cssRule(" a") {
        Modifier.display(DisplayStyle.Flex).gap(8.px).color(RdColor.Ink).backgroundColor(RdColor.Paper)
            .border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(999.px).textDecorationLine(TextDecorationLine.None)
            .css("align-items" to "center", "font-size" to ".9rem", "font-weight" to 500, "padding" to "8px 12px")
    }
    cssRule(" a:hover") { Modifier.css("border-color" to "rgb(30,40,52)") }
}

// ---------- Topics: the question groups ----------

val FaqGroupsStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(48.px)
        .css("max-width" to "860px", "padding-block" to "clamp(24px, 3vw, 40px) clamp(56px, 7vw, 88px)")
}

val FaqGroupStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Grid).gap(16.px).css("scroll-margin-top" to "96px")
}

/** Topic number in logo blue, then the topic heading. */
val FaqGroupHeadStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).gap(14.px).css("align-items" to "baseline") }
    cssRule(" span") {
        Modifier.color(RdColor.LogoBlue).fontFamily(*RdFont.DISPLAY)
            .css("font-size" to "1.6rem", "font-weight" to 900, "font-stretch" to "118%", "line-height" to 1)
    }
}

/** The questions of a topic, in one white card. Works with [RdFaqListStyle] for the open/close rows. */
val FaqCardStyle = CssStyle {
    base {
        Modifier.backgroundColor(RdColor.Paper).border(1.px, LineStyle.Solid, RdColor.Line).borderRadius(16.px)
            .css("padding" to "0 22px")
    }
    cssRule(" details:last-child") { Modifier.css("border-bottom" to "0") }
}

val FaqLinksStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex).gap(10.px).css("flex-wrap" to "wrap") }
    cssRule(" a") {
        Modifier.display(DisplayStyle.Flex).color(RdColor.Brand).backgroundColor(RdColor.SkySoft).borderRadius(10.px)
            .textDecorationLine(TextDecorationLine.None)
            .css("align-items" to "center", "padding" to "10px 14px", "border" to "1px solid rgb(180,198,220)", "font-weight" to 600, "font-size" to ".94rem")
    }
    cssRule(" a:hover") { Modifier.css("border-color" to "rgb(46,75,110)") }
}

/** Search words highlighted in the questions and answers. */
val FaqHitStyle = CssStyle.base {
    Modifier.backgroundColor(RdColor.LogoBlue).color(RdColor.Ink).borderRadius(3.px).css("padding" to "0 2px")
}

val FaqNoResultsStyle = CssStyle {
    base {
        Modifier.backgroundColor(RdColor.SkySoft).color(RdColor.Ink).borderRadius(16.px)
            .css("padding" to "28px", "border" to "1px dashed rgb(180,198,220)")
    }
    cssRule(" a") { Modifier.color(RdColor.Brand).css("font-weight" to 600) }
}
